// Audio management for CTM Chat Widget
export class AudioManager {
  constructor(logger) {
    this.logger = logger || console;
    this.audioContext = null;
    this.soundBuffers = new Map(); // Store multiple sound buffers by name
    this.isInitialized = false;
  }

  // Initialize audio context on first user interaction
  async initialize() {
    if (this.isInitialized) return true;

    try {
      if (!window.AudioContext) {
        this.logger.warn('AudioContext not supported in this browser');
        return false;
      }

      this.audioContext = new AudioContext();
      this.isInitialized = true;
      
      // Resume context if it's suspended (common in browsers)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      
      return true;
    } catch (error) {
      this.logger.error('Failed to initialize AudioContext:', error);
      return false;
    }
  }

  // Load a sound from URL and store it
  async loadSound(name, url) {
    if (!url) return false;

    // Initialize if needed
    if (!this.audioContext) {
      const initialized = await this.initialize();
      if (!initialized) return false;
    }

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch sound: ${response.status}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      
      this.soundBuffers.set(name, audioBuffer);
      this.logger.debug(`Sound loaded: ${name}`);
      return true;
    } catch (error) {
      this.logger.error(`Error loading sound ${name}:`, error);
      return false;
    }
  }

  // Play a loaded sound by name
  async playSound(name) {
    const buffer = this.soundBuffers.get(name);
    if (!buffer || !this.audioContext) {
      this.initialize();
    }

    try {
      // Resume context if suspended
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // Create and play the sound
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(this.audioContext.destination);
      source.start(0);
      
      return true;
    } catch (error) {
      this.logger.error(`Error playing sound ${name}:`, error);
      return false;
    }
  }

  // Play a sound directly from a buffer (legacy support)
  async playSoundBuffer(buffer) {
    if (!buffer || !this.audioContext) {
      this.logger.warn('Cannot play sound: no buffer or audio not initialized');
      return false;
    }

    try {
      // Resume context if suspended
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // Create and play the sound
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(this.audioContext.destination);
      source.start(0);
      
      return true;
    } catch (error) {
      this.logger.error('Error playing sound buffer:', error);
      return false;
    }
  }

  // Check if a sound is loaded
  hasSound(name) {
    return this.soundBuffers.has(name);
  }

  // Get a sound buffer by name
  getSoundBuffer(name) {
    return this.soundBuffers.get(name);
  }

  // Clean up resources
  async cleanup() {
    if (this.audioContext) {
      try {
        await this.audioContext.close();
      } catch (error) {
        this.logger.warn('Error closing AudioContext:', error);
      }
      this.audioContext = null;
    }
    
    this.soundBuffers.clear();
    this.isInitialized = false;
  }

  // Get audio context state
  getState() {
    return {
      initialized: this.isInitialized,
      contextState: this.audioContext?.state || 'closed',
      loadedSounds: Array.from(this.soundBuffers.keys())
    };
  }
}
